import { Injectable, computed, inject, signal } from '@angular/core';
import { Observable, tap, catchError, throwError, map, switchMap } from 'rxjs';
import { PaginatedResponse, PaginationMeta } from '@shared/models';
import { ConversationsApiService } from './conversations-api.service';
import {
  ConversationDetailDto,
  ConversationThreadEntity,
  ConversationMessageEntity,
  ConversationFilter,
} from '../models';

@Injectable({
  providedIn: 'root',
})
export class ConversationsService {
  private readonly api = inject(ConversationsApiService);

  // Private signals
  private readonly _threads = signal<ConversationThreadEntity[]>([]);
  private readonly _selectedThread = signal<ConversationThreadEntity | null>(null);
  private readonly _messages = signal<ConversationMessageEntity[]>([]);
  private readonly _loading = signal(false);
  private readonly _detailLoading = signal(false);
  private readonly _loadingMore = signal(false);
  private readonly _sending = signal(false);
  private readonly _meta = signal<PaginationMeta | null>(null);
  private readonly _error = signal<string | null>(null);
  private readonly _hasMore = signal(false);

  // Public readonly signals
  readonly threads = this._threads.asReadonly();
  readonly selectedThread = this._selectedThread.asReadonly();
  readonly messages = this._messages.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly detailLoading = this._detailLoading.asReadonly();
  readonly loadingMore = this._loadingMore.asReadonly();
  readonly sending = this._sending.asReadonly();
  readonly meta = this._meta.asReadonly();
  readonly error = this._error.asReadonly();
  readonly hasMore = this._hasMore.asReadonly();

  // Computed signals
  readonly totalRecords = computed(() => this._meta()?.total ?? 0);
  readonly isEmpty = computed(() => this._threads().length === 0 && !this._loading());
  readonly totalUnread = computed(() =>
    this._threads().reduce((sum, t) => sum + t.unreadCount, 0)
  );

  loadThreads(filter: ConversationFilter = {}): Observable<PaginatedResponse<ConversationThreadEntity>> {
    this._loading.set(true);
    this._error.set(null);

    return this._fetchThreads(filter).pipe(
      tap(() => this._loading.set(false)),
      catchError((error) => {
        this._loading.set(false);
        this._error.set(error?.error?.message ?? 'Failed to load threads');
        return throwError(() => error);
      })
    );
  }

  /** Silently refresh threads without showing loading spinner (used for SSE updates). */
  refreshThreads(filter: ConversationFilter = {}): Observable<PaginatedResponse<ConversationThreadEntity>> {
    return this._fetchThreads(filter);
  }

  private _fetchThreads(filter: ConversationFilter): Observable<PaginatedResponse<ConversationThreadEntity>> {
    return this.api.getThreads({ limit: 50, ...filter }).pipe(
      map((response) => ({
        ...response,
        data: response.data.map((dto) => new ConversationThreadEntity(dto)),
      })),
      tap((response) => {
        this._threads.set(this.sortByNewest(response.data));
        this._meta.set(response.meta);
      })
    );
  }

  selectThread(threadId: string): Observable<ConversationDetailDto> {
    this._detailLoading.set(true);

    // Find the thread from the list to set as selected
    const thread = this._threads().find((t) => t.id === threadId) ?? null;
    this._selectedThread.set(thread);

    return this.api.getThread(threadId).pipe(
      tap((response) => {
        const messages = response.messages.map((dto) => new ConversationMessageEntity(dto));
        this._messages.set(messages);
        this._hasMore.set(response.hasMore);
        this._detailLoading.set(false);

        // Update unread count in threads list
        if (thread) {
          this._threads.update((threads) =>
            threads.map((t) =>
              t.id === threadId
                ? new ConversationThreadEntity({ ...t, unreadCount: 0 })
                : t
            )
          );
          this._selectedThread.set(
            new ConversationThreadEntity({ ...thread, unreadCount: 0 })
          );
        }

      }),
      catchError((error) => {
        this._detailLoading.set(false);
        return throwError(() => error);
      })
    );
  }

  /** Silently reload messages for the currently selected thread (e.g. on SSE event).
   *  Does NOT show loading spinner to avoid UI flickering. */
  refreshSelectedThread(): Observable<ConversationDetailDto> | null {
    const selectedId = this._selectedThread()?.id;
    if (!selectedId) return null;

    const thread = this._threads().find((t) => t.id === selectedId) ?? null;

    return this.api.getThread(selectedId).pipe(
      tap((response) => {
        const messages = response.messages.map((dto) => new ConversationMessageEntity(dto));
        this._messages.set(messages);
        this._hasMore.set(response.hasMore);

        // Update unread count in threads list
        if (thread) {
          this._threads.update((threads) =>
            threads.map((t) =>
              t.id === selectedId
                ? new ConversationThreadEntity({ ...t, unreadCount: 0 })
                : t
            )
          );
          this._selectedThread.set(
            new ConversationThreadEntity({ ...thread, unreadCount: 0 })
          );
        }
      }),
      catchError((error) => throwError(() => error))
    );
  }

  /** Load older messages (before the oldest currently loaded message). */
  loadOlderMessages(): Observable<ConversationDetailDto> | null {
    const selectedId = this._selectedThread()?.id;
    const currentMessages = this._messages();
    if (!selectedId || !this._hasMore() || this._loadingMore() || currentMessages.length === 0) {
      return null;
    }

    this._loadingMore.set(true);
    const oldestId = currentMessages[0].id;

    return this.api.getThread(selectedId, oldestId).pipe(
      tap((response) => {
        const olderMessages = response.messages.map((dto) => new ConversationMessageEntity(dto));
        this._messages.update((current) => [...olderMessages, ...current]);
        this._hasMore.set(response.hasMore);
        this._loadingMore.set(false);
      }),
      catchError((error) => {
        this._loadingMore.set(false);
        return throwError(() => error);
      })
    );
  }

  reply(threadId: string, message: string): Observable<void> {
    this._sending.set(true);

    return this.api.reply(threadId, message).pipe(
      switchMap(() => this.api.getThread(threadId)),
      tap((response) => {
        const messages = response.messages.map((dto) => new ConversationMessageEntity(dto));
        this._messages.set(messages);
        this._hasMore.set(response.hasMore);
        this._sending.set(false);

        // Update thread status and last message in list, then sort by newest
        const now = new Date().toISOString();
        this._threads.update((threads) => {
          const updated = threads.map((t) => {
            if (t.id !== threadId) return t;
            return new ConversationThreadEntity({
              ...t,
              status: 'answered',
              lastMessagePreview: message,
              lastActivityAt: now,
              unreadCount: 0,
            });
          });
          return this.sortByNewest(updated);
        });

        // Update selected thread too
        const current = this._selectedThread();
        if (current && current.id === threadId) {
          this._selectedThread.set(
            new ConversationThreadEntity({
              ...current,
              status: 'answered',
              lastMessagePreview: message,
              lastActivityAt: now,
              unreadCount: 0,
            })
          );
        }
      }),
      map(() => void 0),
      catchError((error) => {
        this._sending.set(false);
        return throwError(() => error);
      })
    );
  }

  closeThread(threadId: string): Observable<void> {
    return this.api.close(threadId).pipe(
      tap(() => {
        // Update in threads list
        this._threads.update((threads) =>
          threads.map((t) => {
            if (t.id !== threadId) return t;
            return new ConversationThreadEntity({
              ...t,
              status: 'closed',
              unreadCount: 0,
            });
          })
        );

        // Update selected thread
        const current = this._selectedThread();
        if (current && current.id === threadId) {
          this._selectedThread.set(
            new ConversationThreadEntity({
              ...current,
              status: 'closed',
              unreadCount: 0,
            })
          );
        }
      }),
      catchError((error) => throwError(() => error))
    );
  }

  /** Send typing indicator — delegates to API service */
  sendTyping(threadId: string): Observable<void> {
    return this.api.sendTyping(threadId);
  }

  clearSelection(): void {
    this._selectedThread.set(null);
    this._messages.set([]);
    this._hasMore.set(false);
  }

  clearError(): void {
    this._error.set(null);
  }

  /** Sort threads by lastActivityAt descending (newest message first). */
  private sortByNewest(threads: ConversationThreadEntity[]): ConversationThreadEntity[] {
    return [...threads].sort(
      (a, b) => new Date(b.lastActivityAt).getTime() - new Date(a.lastActivityAt).getTime()
    );
  }
}
